import React, { useEffect, useState } from "react";
import { AutoComplete, Button, Input, Space, Spin, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import BusinessResourceNotice from "../BusinessResourceNotice";
import { FormOrderType, OrderChoiceProduct, useOrderChoiceResource } from "./useOrderFormOptions";
import { isOffline } from "../../utils/offlineQueue";

export default function OrderProductSearch({ orderType, action, active, disabled, className, onSelect }: {
  orderType: FormOrderType; action: "create" | "edit"; active: boolean; disabled?: boolean;
  className?: string;
  onSelect: (product: OrderChoiceProduct) => void;
}) {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const sales = ["sales", "proforma"].includes(orderType);
  useEffect(() => {
    if (orderType === "proforma") { setSearch(""); setAppliedSearch(""); setPage(1); }
  }, [active, action, orderType]);
  useEffect(() => {
    const timer = setTimeout(() => { setAppliedSearch(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const resource = useOrderChoiceResource({ orderType, action, active, enabled: !disabled,
    parameters: { kind: "products", search: appliedSearch, page, limit: 20 } });
  const choices = resource.data?.products || [];
  return <>
    <BusinessResourceNotice resource={resource} label="les articles disponibles" />
    <AutoComplete className={className} style={{ width: "100%" }} disabled={disabled} value={search}
      onSearch={setSearch} onChange={setSearch} filterOption={false}
      onDropdownVisibleChange={(open) => {
        if (open && active && !disabled && orderType === "proforma") void resource.refresh().catch(() => {});
      }}
      options={choices.map((product) => ({ value: String(product.id), product,
        disabled: orderType === "proforma" && resource.status !== "verified",
        label: <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{product.name}{product.item_type === "service" ? " · Service" : ""}</span>
          <Typography.Text type="secondary">
            {product.current_stock === undefined ? "" : `Stock : ${product.current_stock.toLocaleString("fr-FR")} · `}
            {(sales ? product.prix_vente : product.purchase_price)?.toLocaleString("fr-FR")} CFA
          </Typography.Text>
        </Space>,
      }))}
      onSelect={(_, option) => {
        if (resource.status !== "verified" && !(orderType === "sales" && isOffline() && resource.data?.freshness === "cached")) return;
        onSelect(option.product); setSearch(""); setAppliedSearch(""); setPage(1);
      }}
      notFoundContent={resource.loading ? <Spin size="small" /> : resource.error
        ? "Articles non vérifiés. Réessayez." : "Aucun article trouvé"}
      dropdownRender={(menu) => <>
        {menu}
        {(page > 1 || resource.data?.hasMore) && <Space style={{ padding: 8 }}>
          <Button size="small" disabled={page === 1 || resource.loading} onMouseDown={(event) => event.preventDefault()}
            onClick={() => setPage((value) => value - 1)}>Précédents</Button>
          <Button size="small" disabled={!resource.data?.hasMore || resource.loading} onMouseDown={(event) => event.preventDefault()}
            onClick={() => setPage((value) => value + 1)}>Suivants</Button>
        </Space>}
      </>}
    >
      <Input placeholder={sales ? "Rechercher un produit ou un service..." : "Rechercher un produit..."}
        prefix={<SearchOutlined />} suffix={resource.loading ? <Spin size="small" /> : null} />
    </AutoComplete>
  </>;
}
